"use client";

import { useContext, useState } from "react";
import PropTypes from "prop-types";
import { Hero, Toast, Header } from "@components";
import { SectionContext } from "@contexts";
import { Parallax, ParallaxLayer } from "@react-spring/parallax";

// TODO: Pull this from either a util storage file or cms
import { Primary as headerStory } from "@components/layouts/Header/Header.stories";
const headerProps = headerStory?.args;

import dynamic from "next/dynamic";
const TechnologiesSection = dynamic(
  () => import("@components/composites/TechnologiesSection"),
  { ssr: false }
);
const ProjectsSection = dynamic(
  () => import("@components/composites/ProjectsSection/ProjectsSection"),
  { ssr: false }
);
const CertificationsSection = dynamic(
  () => import("@components/composites/CertificationsSection"),
  { ssr: false }
);
const ConnectSection = dynamic(
  () => import("@components/composites/ConnectSection"),
  { ssr: false }
);
const ContactSection = dynamic(
  () => import("@components/composites/ContactSection"),
  { ssr: false }
);

import { Primary } from "@components/composites/ConnectSection/ConnectSection.stories";
const connectCards = Primary?.args?.connectCards;

export default function BodyScroller({
  heroProps,
  projectCards,
  techCards,
  certCards,
}) {
  const { parallaxScrollerRef } = useContext(SectionContext);

  const [toastProperties, setToastProperties] = useState({
    severity: "success",
    open: false,
    message: "",
  });
  const [hasSentMessage, setHasSentMessage] = useState(false);
  const [messagePending, setMessagePending] = useState(false);

  const handleToastClose = () => {
    setToastProperties((prev) => ({ ...prev, open: false }));
  };

  const handleEmailSubmit = async (formBody) => {
    if (messagePending) return;
    setMessagePending(true);
    const res = await fetch("/api/contact", {
      method: "POST",
      body: JSON.stringify(formBody),
    });
    setMessagePending(false);

    if (res.ok) {
      setToastProperties({
        open: true,
        severity: "success",
        message:
          "Message sent successfully, I'll get back to you as soon as I can!",
      });
      setHasSentMessage(true);
      return;
    }
    setToastProperties({
      open: true,
      severity: "error",
      message:
        "There was an error sending your message, please try again later",
    });
    return;
  };

  return (
    <>
      <Parallax
        pages={6.2}
        ref={parallaxScrollerRef}
        style={{ top: "0", left: "0" }}
      >
        <ParallaxLayer
          sticky={{ start: 0, end: 500 }}
          style={{
            display: "flex",
            justifyContent: "start",
            alignItems: "start",
            height: "fit-content",
          }}
        >
          <Header {...headerProps} />
        </ParallaxLayer>
        <Hero {...heroProps} key="hero-section" id="section-0" />

        <ProjectsSection projectCards={projectCards} />

        <TechnologiesSection techCards={techCards} />

        <CertificationsSection
          offset={3}
          speed={0.3}
          certificationsCards={certCards}
        />

        <ConnectSection offset={4} speed={0.3} connectCards={connectCards} />

        <ContactSection
          offset={5}
          factor={1.2}
          speed={{ foreground: 0.3, background: 0 }}
          handleSubmit={handleEmailSubmit}
          hasSentMessage={hasSentMessage}
          sendMessageLoading={messagePending}
        />
      </Parallax>
      <Toast
        open={toastProperties.open}
        severity={toastProperties.severity}
        onClose={handleToastClose}
      >
        {toastProperties.message}
      </Toast>
    </>
  );
}

BodyScroller.propTypes = {
  heroProps: PropTypes.instanceOf(Object),
  projectCards: PropTypes.arrayOf(PropTypes.instanceOf(Object)),
  techCards: PropTypes.arrayOf(PropTypes.instanceOf(Object)),
  certCards: PropTypes.arrayOf(PropTypes.instanceOf(Object)),
};
